import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { normalizePhone } from "@/utils/phoneUtils";
import { toast } from "sonner";

interface NewConversationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialPhone?: string;
  selectedInstance?: string | null;
  onSuccess?: () => void;
}

export function NewConversationDialog({
  open,
  onOpenChange,
  initialPhone = "",
  selectedInstance,
  onSuccess,
}: NewConversationDialogProps) {
  const [phone, setPhone] = useState(initialPhone);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const { currentOrg } = useOrganizationContext();

  useEffect(() => {
    if (open) {
      setPhone(initialPhone);
      setMessage("");
    }
  }, [open, initialPhone]);

  const handleSendMessage = async () => {
    if (!phone.trim() || !message.trim()) {
      toast.error('Preencha o número e a mensagem.');
      return;
    }

    if (!currentOrg?.id) {
      toast.error('Organização não encontrada.');
      return;
    }
    
    // Buscar automaticamente a instância WhatsApp com agent_enabled
    let instanceToUse = selectedInstance;
    
    try {
      // Primeiro, verificar se a instância selecionada é válida
      if (selectedInstance) {
        const { data: instanceData } = await supabase
          .from('instances')
          .select('channel, integration_type, agent_enabled, org_id, status')
          .eq('id', selectedInstance)
          .single();
        
        if (instanceData) {
          const isWhatsApp = instanceData.channel === 'whatsapp' || 
                           instanceData.integration_type === 'WHATSAPP-BAILEYS';
          const isValid = isWhatsApp && 
                         instanceData.agent_enabled === true && 
                         instanceData.org_id === currentOrg.id &&
                         instanceData.status === 'open';
          
          if (isValid) {
            instanceToUse = selectedInstance;
          }
        }
      }
      
      // Se a instância selecionada não for válida, buscar automaticamente uma WhatsApp
      if (!instanceToUse) {
        const { data: whatsappInstances } = await supabase
          .from('instances')
          .select('id, channel, integration_type, agent_enabled')
          .eq('org_id', currentOrg.id)
          .eq('status', 'open')
          .order('created_at', { ascending: false });
        
        if (whatsappInstances && whatsappInstances.length > 0) {
          // Prioridade 1: WhatsApp com agent_enabled ligado
          const whatsappWithAgent = whatsappInstances.find(inst => {
            const isWhatsApp = inst.channel === 'whatsapp' || inst.integration_type === 'WHATSAPP-BAILEYS';
            return isWhatsApp && inst.agent_enabled === true;
          });
          
          if (whatsappWithAgent) {
            instanceToUse = whatsappWithAgent.id;
          } else {
            // Prioridade 2: Qualquer WhatsApp
            const anyWhatsApp = whatsappInstances.find(inst => 
              inst.channel === 'whatsapp' || inst.integration_type === 'WHATSAPP-BAILEYS'
            );
            
            if (anyWhatsApp) {
              instanceToUse = anyWhatsApp.id;
            }
          }
        }
      }
      
      if (!instanceToUse) {
        toast.error('Nenhuma instância do WhatsApp com Agente Fluity habilitado encontrada.');
        return;
      }
      
      // Verificar novamente a instância que será usada
      const { data: finalInstanceData } = await supabase
        .from('instances')
        .select('channel, integration_type, agent_enabled')
        .eq('id', instanceToUse)
        .single();
      
      if (!finalInstanceData) {
        toast.error('Instância não encontrada.');
        return;
      }
      
      const isWhatsApp = finalInstanceData.channel === 'whatsapp' || 
                        finalInstanceData.integration_type === 'WHATSAPP-BAILEYS';
      
      if (!isWhatsApp) {
        toast.error('O envio de mensagens só está disponível para instâncias do WhatsApp.');
        return;
      }
      
      if (!finalInstanceData.agent_enabled) {
        toast.error('O envio de mensagens só está disponível quando o Agente Fluity está habilitado na instância WhatsApp.');
        return;
      }
    } catch (error) {
      console.error('Erro ao verificar tipo da instância:', error);
      toast.error('Erro ao verificar instância. Tente novamente.');
      return;
    }
    
    let cleanPhone = phone.trim().replace(/\D/g, '');
    
    // Se o número tem 10 ou 11 dígitos (formato DDD + número), assumir que é Brasil e adicionar 55
    if (cleanPhone.length === 10 || cleanPhone.length === 11) {
      cleanPhone = '55' + cleanPhone;
    }

    if (cleanPhone.length < 10) {
      toast.error('Número de telefone inválido. Use o formato com código do país (ex: 5582999999999).');
      return;
    }
    
    // Aplicar a normalização do 9º dígito para compatibilidade com Evolution API/WhatsApp
    const phoneToSubmit = normalizePhone(cleanPhone);
    
    setSending(true);
    
    try {
      // 0. Buscar configurações da Evolution API para a organização
      let evolutionApiUrl = import.meta.env.VITE_EVOLUTION_API_URL || 'https://api.fluity.space';
      let evolutionApiToken = import.meta.env.VITE_EVOLUTION_API_TOKEN || '77fbaccc54b75e38a55c2ec568ccd36e';

      // Se o token for um placeholder do env.example, usar o token padrão
      if (evolutionApiToken === 'your-evolution-api-token-here') {
        evolutionApiToken = '77fbaccc54b75e38a55c2ec568ccd36e';
      }

      if (currentOrg?.id) {
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('evolution_api_url, evolution_api_token')
          .eq('id', currentOrg.id)
          .single();
        
        if (orgError) {
          console.error('❌ Erro ao buscar configurações da organização:', orgError);
        } else if (orgData) {
          if (orgData.evolution_api_url) evolutionApiUrl = orgData.evolution_api_url;
          if (orgData.evolution_api_token) evolutionApiToken = orgData.evolution_api_token;
        }
      }

      // 1. Enviar via Evolution API
      console.log('🚀 Enviando mensagem via Evolution API:', {
        url: `${evolutionApiUrl}/message/sendText/${instanceToUse}`,
        number: phoneToSubmit,
        instance: instanceToUse
      });

      const response = await fetch(
        `${evolutionApiUrl}/message/sendText/${instanceToUse}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': evolutionApiToken,
          },
          body: JSON.stringify({
            number: phoneToSubmit,
            text: message.trim() // Removido o nome do atendente conforme solicitado
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Erro detalhado da Evolution API:', errorData);
        throw new Error(errorData.message?.[0] || errorData.message || 'Erro ao enviar mensagem via Evolution API');
      }

      const responseData = await response.json();
      const whatsappMessageId = responseData?.key?.id || responseData?.id || responseData?.messageId || null;
      
      // 2. Chamar webhook
      if (currentOrg?.id) {
        try {
          const { getWebhookUrl: getWH, WEBHOOK_IDS: WH_IDS } = await import('@/lib/webhookRegistry')
          const webhookUrl = await getWH(WH_IDS.NEW_CONVERSATION, 'https://webhook.fluity.space/webhook/0bb6367d-3f8a-4cd2-8ba1-d8e684b51164');
          await fetch(webhookUrl, {
            method: 'POST',
            mode: 'no-cors', // Adicionado no-cors para evitar erro de CORS em chamadas de webhook
            headers: {
              'Content-Type': 'text/plain', // Usar text/plain evita o preflight OPTIONS
            },
            body: JSON.stringify({
              phone: phoneToSubmit,
              org_id: currentOrg.id,
              message: message.trim()
            })
          });
        } catch (webhookError) {
          console.error('Erro ao chamar webhook:', webhookError);
        }
      }
      
      const now = new Date().toISOString();
      const normalizedPhone = normalizePhone(phoneToSubmit);
      
      // 3. Criar ou atualizar contato
      if (currentOrg?.id && user) {
        const { data: allContacts } = await supabase
          .from('contacts')
          .select('id, phone, instance_id')
          .eq('org_id', currentOrg.id);
        
        const existingContact = allContacts?.find(contact => 
          normalizePhone(contact.phone) === normalizedPhone && normalizedPhone.length > 0
        );

        if (existingContact) {
          await supabase
            .from('contacts')
            .update({
              updated_at: now,
              whatsapp: normalizedPhone,
              instance_id: existingContact.instance_id || instanceToUse
            })
            .eq('id', existingContact.id);
        } else {
        await supabase
          .from('contacts')
          .insert({
            org_id: currentOrg.id,
            phone: phoneToSubmit,
            whatsapp: normalizedPhone,
            name: normalizedPhone,
            instance_id: instanceToUse,
            created_at: now,
            updated_at: now
          });
        }
      }
      
      // 4. Tabela chat_messages removida; envio via API/evolução (mensagem já enviada pelo cliente)
      toast.success('Mensagem enviada com sucesso!');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error('Erro ao enviar nova mensagem:', error);
      toast.error('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Iniciar Nova Conversa</DialogTitle>
          <DialogDescription>
            Digite o número do WhatsApp e a mensagem que deseja enviar
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="phone">Número do WhatsApp</Label>
            <Input
              id="phone"
              placeholder="Ex: 5582999999999 (com código do país)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={sending}
            />
            <p className="text-xs text-muted-foreground">
              Digite o número completo com código do país (ex: 5582999999999)
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="message">Mensagem</Label>
            <Textarea
              id="message"
              placeholder="Digite sua mensagem..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={sending}
              rows={4}
              className="resize-none"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSendMessage}
            disabled={!phone.trim() || !message.trim() || sending}
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Enviando...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Enviar Mensagem
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
